import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Shield, Clock, Building2 } from 'lucide-react';
import { PageSEO } from '../components/SEO';

/**
 * UeberUns - TasteSkill Premium Redesign
 * 
 * Verbesserungen:
 * - Professionelleres Layout passend zur V3-Final Startseite
 * - Bessere visuelle Hierarchie
 * - Einheitliche Designsprache
 */

const UeberUns = () => {
  return (
    <>
      <PageSEO 
        title="Über uns – Firmbay Unternehmensvermittlung"
        description="Firmbay ist Ihr Partner für GmbH-Vermittlung in Deutschland. Langjährige Erfahrung und persönliche Begleitung bei GmbH-Kauf, -Verkauf und -Gründung."
        path="/ueber-uns"
      />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-24 pb-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFA] to-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E9F7F6] rounded-full mb-4">
              <Building2 className="w-3.5 h-3.5 text-[#009E9A]" />
              <span className="text-[11px] font-semibold text-[#009E9A] uppercase tracking-wider">
                Über uns
              </span>
            </div>
            <h1 className="text-[28px] md:text-[34px] font-bold text-[#0B1930] mb-4 leading-tight tracking-[-0.02em]">
              Firmbay – Unternehmensvermittlung
            </h1>
            <p className="text-base text-gray-600 leading-relaxed">
              Wir sind Ihr Partner für die Vermittlung von GmbH-Gründungen und Bestandsgesellschaften in Deutschland. 
              Firmbay verbindet langjährige berufliche und unternehmerische Erfahrung mit einer persönlichen und 
              strukturierten Begleitung bei der Vermittlung von Gesellschaften.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6]">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Clock,
                title: '30 Jahre Berufserfahrung',
                text: 'Langjährige Erfahrung in der Unternehmensberatung und Begleitung von Gesellschaftsgründungen.'
              },
              {
                icon: Users,
                title: 'Persönliche Betreuung',
                text: 'Direkter Ansprechpartner während des gesamten Vermittlungsprozesses.'
              },
              {
                icon: Shield,
                title: 'Strukturierter Ablauf',
                text: 'Hohe Standards bei der Begleitung und Koordination Ihrer Gründung oder Übernahme.'
              }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#009E9A]" />
                  </div>
                  <h3 className="font-bold text-[#0B1930] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-4">
                Unsere Mission
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
                <p>
                  Wir begleiten Gründer und Unternehmer auf dem Weg zur eigenen GmbH oder bei der Übernahme 
                  einer bestehenden Gesellschaft. Unsere Mission ist es, den Vermittlungsprozess so strukturiert 
                  und transparent wie möglich zu gestalten.
                </p>
                <p>
                  Wir glauben, dass jeder mit einer guten Geschäftsidee die Möglichkeit haben sollte, 
                  sein Unternehmen professionell zu gründen. Deshalb bieten wir umfassende 
                  Begleitung und Unterstützung zu fairen Preisen.
                </p>
                <p>
                  Bei Bedarf können im Rahmen der Abwicklung externe Rechtsanwälte, Steuerberater, 
                  Notare oder weitere fachkundige Stellen eingebunden werden.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              {[
                {
                  title: 'Diskrete Vermittlung',
                  text: 'Unternehmensinformationen werden im Rahmen des Vermittlungsprozesses gezielt bereitgestellt.'
                },
                {
                  title: 'Persönliche Begleitung',
                  text: 'Direkter Ansprechpartner während des Vermittlungsprozesses.'
                },
                {
                  title: 'Strukturierter Ablauf',
                  text: 'Wir koordinieren die nächsten Schritte zwischen Interessenten, Anbietern und den beteiligten Stellen.'
                }
              ].map((item, idx) => (
                <div key={idx} className="border-l-2 border-[#009E9A] pl-4">
                  <h3 className="font-semibold text-[#0B1930] mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Geschäftsführer Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F8FAFA] border-y border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-8">
            Geschäftsführung
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="w-28 h-28 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-gray-400 text-xs text-center px-2">Foto folgt</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0B1930]">Tobias Pohl</h3>
              <p className="text-[#009E9A] text-sm font-medium mb-3">Geschäftsführer · Interpohl Solutions GmbH</p>
              <p className="text-gray-600 leading-relaxed max-w-xl text-[15px]">
                Mit 30 Jahren Berufserfahrung in der Unternehmensberatung begleitet Tobias Pohl 
                Gründer und Unternehmer bei der Vermittlung von Gesellschaften – persönlich, 
                strukturiert und diskret.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experten Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-4">
            Zusammenarbeit mit Experten
          </h2>
          <p className="text-gray-600 leading-relaxed mb-6 max-w-3xl text-[15px]">
            Bei Bedarf können im Rahmen der Abwicklung externe Rechtsanwälte, Steuerberater, 
            Notare oder weitere fachkundige Stellen eingebunden werden. So stellen wir sicher, 
            dass Sie die für Ihre Situation passende Unterstützung erhalten.
          </p>
          
          <div className="p-5 border border-gray-200 rounded-xl bg-[#F8FAFA]">
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong className="text-[#0B1930]">Hinweis:</strong> Firmbay bietet keine Rechts- oder Steuerberatung an. 
              Die Plattform dient der Vermittlung und organisatorischen Begleitung. 
              Gesellschaftsrechtliche Vorgänge erfolgen durch Notare und externe Partner.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0B1930]">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
            Lassen Sie sich beraten
          </h2>
          <p className="text-gray-300 mb-6">
            Egal ob Sie eine GmbH gründen, kaufen oder verkaufen möchten – wir helfen Ihnen weiter.
          </p>
          <Link to="/kontakt">
            <Button className="bg-white text-[#0B1930] hover:bg-[#E9F7F6] rounded-lg text-sm px-6 py-2.5">
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default UeberUns;
